import React, { useState, useEffect } from "react";
import { Modal, Box } from "@mui/material";
import { FaCheckCircle, FaLock } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import CHECK from "../../../assets/img/payout/check.png";
import CROSS from "../../../assets/img/payout/reject.png";
import axios from "axios";
import Swal from "sweetalert2";
import BtnLoader from "Components/Common/Loader/BtnLoader";
import { IoCopy } from "react-icons/io5";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "95%",
  maxWidth: "600px",
  maxHeight: "90vh",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: 8,
  boxShadow: 24,
  p: 3,
  overflowY: "auto",
};

const steps = ["Basic Info", "Details", "OTP Verification", "UPI Info"];

const KycPopup = ({
  open,
  onClose,
  user,
  refreshData,
  currentTabPermissions,
  staffId,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState({ approve: false, reject: false });
  const [showApproveFields, setShowApproveFields] = useState(false);
  const [showRejectFields, setShowRejectFields] = useState(false);
  const [copied, setCopied] = useState(false);

  // Approve fields
  const [beneficiaryName, setBeneficiaryName] = useState("");
  const [upiVerified, setUpiVerified] = useState(false);

  // Reject fields
  const [reKycFor, setReKycFor] = useState("");

  const [approveErrors, setApproveErrors] = useState({
    beneficiaryName: "",
    upiVerified: "",
  });
  const [rejectError, setRejectError] = useState("");

  useEffect(() => {
    if (!open || !user) return;

    // Set current step based on user
    if (user?.currentVerifyingStep === 2) {
      if (!user?.isPANVerified) setCurrentStep(0);
      else if (user?.isPANVerified && !user?.firstName) setCurrentStep(0);
      else if (user?.isPANVerified && user?.isOTPVerified && user?.firstName)
        setCurrentStep(2);
      else setCurrentStep(1);
    } else if (user?.currentVerifyingStep === 4) setCurrentStep(3);
    else setCurrentStep(0);

    setShowApproveFields(false);
    setShowRejectFields(false);
    setBeneficiaryName("");
    setUpiVerified(false);
    setReKycFor("");
  }, [open, user]);

  if (!open || !user) return null;

  const refreshAndClose = () => {
    refreshData?.();
    onClose();
  };

  const createStaffActivityLog = async (actionType, description) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/staffs-activity-log/create-staffs-activity`,
        {
          staff: staffId,
          action: actionType,
          description,
        }
      );
    } catch (error) {
      console.error("Activity log failed:", error.message);
    }
  };

  // ✅ APPROVE API
  const handleApprove = async () => {
    let hasError = false;
    const errors = { beneficiaryName: "", upiVerified: "" };

    if (!beneficiaryName.trim()) {
      errors.beneficiaryName = "Beneficiary Name is required";
      hasError = true;
    }
    if (!upiVerified) {
      errors.upiVerified = "Please verify UPI checkbox";
      hasError = true;
    }

    setApproveErrors(errors);
    if (hasError) return;

    setLoading((prev) => ({ ...prev, approve: true }));
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/payout-kyc/approve`, {
        userId: user.userId,
        beneficiaryName,
        isUpiVerified: upiVerified,
      });

      await createStaffActivityLog(
        "KYC_APPROVE",
        `KYC approved for user ${
          user?.firstName
            ? `${user.firstName} ${user.lastName}`
            : user?.panName || ""
        } (Phone: ${user?.phonenumber || "-"})`
      );

      Swal.fire({
        icon: "success",
        title: "Approved Successfully",
        showConfirmButton: true,
      });
      refreshAndClose();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Approval Failed",
        text: err.response?.data?.message || "Something went wrong",
      });
    } finally {
      setLoading((prev) => ({ ...prev, approve: false }));
    }
  };

  // ✅ REJECT API
  const handleReject = async () => {
    if (!reKycFor) {
      setRejectError("Please select ReKYC reason");
      return;
    }

    setLoading((prev) => ({ ...prev, reject: true }));
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/payout-kyc/reject`, {
        userId: user.userId,
        reKycFor,
      });

      await createStaffActivityLog(
        "KYC_REJECT",
        `KYC rejected for user ${
          user?.firstName
            ? `${user.firstName} ${user.lastName}`
            : user?.panName || ""
        } (Reason: ${reKycFor})`
      );

      Swal.fire({
        icon: "success",
        title: "Rejected Successfully",
        showConfirmButton: true,
      });
      refreshAndClose();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Rejection Failed",
        text: err.response?.data?.message || "Something went wrong",
        showConfirmButton: true,
      });
    } finally {
      setLoading((prev) => ({ ...prev, reject: false }));
    }
  };

  const isStepLocked = (index) => {
    if (!user) return true;
    if (["PENDING", "APPROVED", "REJECTED"].includes(user?.status))
      return false;

    if (user?.currentVerifyingStep === 2) {
      if (!user?.isPANVerified) return true;
      if (user?.isPANVerified && !user?.firstName) return index !== 0;
      if (user?.isPANVerified && user?.isOTPVerified && user?.firstName)
        return index > 2;
      return index > 1;
    }
    if (user?.currentVerifyingStep === 4) return false;
    return false;
  };

  const isStepCompleted = (index) => {
    if (!user) return false;
    if (user?.currentVerifyingStep === 2) {
      if (!user?.isPANVerified) return false;
      if (user?.isPANVerified && !user?.firstName) return index === 0;
      if (user?.isPANVerified && user?.isOTPVerified && user?.firstName)
        return index <= 2;
      return index <= 1;
    }
    if (user?.currentVerifyingStep === 4) return index <= 3;
    return index < currentStep;
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={style}
        className="w-full sm:w-[90%] md:w-[80%] lg:w-[70%] xl:w-[600px]"
      >
        <div className="relative h-full w-full">
          <button
            onClick={onClose}
            className="absolute right-3 top-3 text-gray-600 transition-transform hover:rotate-90 hover:text-red-500"
          >
            <IoClose size={28} />
          </button>

          <h2 className="mb-6 text-lg font-bold text-gray-800 sm:text-xl">
            KYC Verification Status
            {user?.status === "PENDING" && (
              <span className="ml-2 text-sm font-semibold text-orange-600 sm:text-base">
                (PENDING)
              </span>
            )}
            {user?.status === "APPROVED" && (
              <span className="ml-2 text-sm font-semibold text-green-600 sm:text-base">
                (Approved)
              </span>
            )}
            {user?.status === "REJECTED" && (
              <span className="ml-2 text-sm font-semibold text-red-600 sm:text-base">
                (Rejected)
              </span>
            )}
          </h2>

          {/* Progress Bar */}
          <div className="m-auto mb-10 flex w-3/4 items-center justify-center">
            {steps.map((step, index) => {
              const locked = isStepLocked(index);
              const completed = isStepCompleted(index);

              return (
                <div key={step} className="flex flex-1 items-center">
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-semibold sm:h-10 sm:w-10 sm:text-sm
                      ${
                        completed
                          ? "border-[#c51369] bg-[#c51369] text-white"
                          : index === currentStep
                          ? "border-[#c51369] text-[#c51369]"
                          : "border-gray-300 text-gray-500"
                      }
                      ${
                        locked
                          ? "cursor-not-allowed opacity-50"
                          : "cursor-pointer"
                      }`}
                    onClick={() => !locked && setCurrentStep(index)}
                  >
                    {locked ? (
                      <FaLock size={12} />
                    ) : completed ? (
                      <FaCheckCircle size={14} />
                    ) : (
                      index + 1
                    )}
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`h-[2px] flex-1 ${
                        completed ? "bg-[#c51369]" : "bg-gray-300"
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* Steps Content */}
          <div className="min-h-[220px] text-sm sm:text-base">
            {/* PAN Step */}
            {currentStep === 0 && user?.isPANVerified && (
              <>
                <h3 className="mb-4 text-center text-base font-bold text-[#a01155] sm:text-lg">
                  PAN Details
                </h3>
                <div className="mx-auto w-full max-w-md space-y-4">
                  <Detail label="Full Name" value={user?.panName} />
                  <Detail label="10 Digit PAN" value={user?.panNumber} />
                </div>
                <p className="mt-16 text-center text-green-600">
                  ✅ PAN is Verified
                </p>
              </>
            )}

            {/* Beneficiary Step */}
            {currentStep === 1 && (
              <div className="mx-auto w-full max-w-xl space-y-4 text-center">
                <h3 className="mb-4 text-base font-bold text-[#a01155] sm:text-lg">
                  Beneficiary Details
                </h3>
                <div className="mx-auto flex w-full max-w-md flex-col space-y-4">
                  <Detail label="First Name" value={user?.firstName} />
                  <Detail label="Last Name" value={user?.lastName} />
                  <Detail label="Phone Number" value={user?.phonenumber} />
                  <Detail label="State" value={user?.state} />
                  <Detail label="Address" value={user?.address} />
                  <Detail label="Pincode" value={user?.pinCode} />
                </div>
              </div>
            )}

            {/* OTP Step */}
            {currentStep === 2 && (
              <div className="flex h-60 flex-col items-center justify-center space-y-4 text-center">
                {user?.isOTPVerified ? (
                  <>
                    <img
                      src={CHECK}
                      alt="success"
                      className="h-16 w-16 sm:h-20 sm:w-20"
                    />
                    <p className="font-semibold text-green-600">
                      Beneficiary Verified Successfully!
                    </p>
                  </>
                ) : (
                  <>
                    <img
                      src={CROSS}
                      alt="failed"
                      className="h-16 w-16 sm:h-20 sm:w-20"
                    />
                    <p className="font-semibold text-red-600">
                      Beneficiary Verification Failed
                    </p>
                  </>
                )}
              </div>
            )}

            {/* {currentStep === 2 && (
              <div className="flex h-60 flex-col items-center justify-center space-y-4 text-center">
                <img
                  src={CHECK}
                  alt="success"
                  className="h-16 w-16 sm:h-20 sm:w-20"
                />
                <p className="text-green-600">
                  {user?.isOTPVerified
                    ? "Beneficiary Verified Successfully!"
                    : "OTP Verification Failed"}
                </p>
              </div>
            )} */}

            {/* UPI Step */}
            {currentStep === 3 && (
              <div className="mx-auto w-full max-w-md space-y-4">
                <h3 className="mb-4 text-center text-base font-bold text-[#a01155] sm:text-lg">
                  UPI Verification Status
                </h3>
                {user?.upiName && (
                  <Detail
                    label="UPI Name"
                    value={
                      <span>
                        {user?.upiName}{" "}
                        {user?.isUPIVerified && (
                          <span className="text-lg text-green-600">✔</span>
                        )}
                      </span>
                    }
                  />
                )}
                <Detail
                  label="PAN Name"
                  value={
                    <span>
                      {user?.panName}{" "}
                      {user?.isUPIVerified && (
                        <span className="text-lg text-green-600">✔</span>
                      )}
                    </span>
                  }
                />
                <div className="mb-2">
                  <Detail
                    label="UPI ID"
                    value={
                      <div className="flex items-center gap-2">
                        <span>{user?.upiId || "-"}</span>
                        {user?.upiId && (
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(user.upiId);
                              setCopied(true);
                              setTimeout(() => setCopied(false), 1500);
                            }}
                            className="text-gray-500 hover:text-[#c51369]"
                          >
                            <IoCopy size={16} />
                          </button>
                        )}
                      </div>
                    }
                  />
                  <div className="mb-6 flex justify-end">
                    {copied && (
                      <p className="mt-1 text-sm text-green-600">Copied!</p>
                    )}
                  </div>
                </div>

                {user?.status === "REJECTED" && (
                  <div className="mt-6 flex justify-center">
                    <button
                      disabled
                      className="cursor-not-allowed rounded-lg bg-gray-400 px-4 py-2 text-white"
                    >
                      Re-KYC
                    </button>
                  </div>
                )}

                {user?.status === "PENDING" &&
                  !showApproveFields &&
                  !showRejectFields &&
                  currentTabPermissions.edit && (
                    <div className="mt-10 flex h-40 items-center justify-center gap-3">
                      <button
                        onClick={() => setShowApproveFields(true)}
                        className="flex min-w-[110px] items-center justify-center rounded-lg bg-[#c51369] px-6 py-2 text-white hover:bg-[#e24692]"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => setShowRejectFields(true)}
                        className="flex min-w-[110px] items-center justify-center rounded-lg bg-red-500 px-6 py-2 text-white hover:bg-red-600"
                      >
                        Reject
                      </button>
                    </div>
                  )}

                {/* Approve Fields */}
                {showApproveFields && (
                  <div className="mt-4 space-y-3">
                    <div className="flex flex-col">
                      <label className="mb-2">Beneficiary Name</label>
                      <input
                        type="text"
                        placeholder="Enter your beneficiary name"
                        value={beneficiaryName}
                        onChange={(e) => {
                          setBeneficiaryName(e.target.value);
                          setApproveErrors((prev) => ({
                            ...prev,
                            beneficiaryName: "",
                          }));
                        }}
                        className="w-3/4  rounded border border-gray-400 px-3 py-2"
                      />
                    </div>

                    {approveErrors.beneficiaryName && (
                      <p className="text-sm text-red-500">
                        {approveErrors.beneficiaryName}
                      </p>
                    )}

                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={upiVerified}
                        onChange={(e) => {
                          setUpiVerified(e.target.checked);
                          setApproveErrors((prev) => ({
                            ...prev,
                            upiVerified: "",
                          }));
                        }}
                      />
                      UPI Verified
                    </label>
                    {approveErrors.upiVerified && (
                      <p className="text-sm text-red-500">
                        {approveErrors.upiVerified}
                      </p>
                    )}

                    <div className="flex justify-center gap-3">
                      <button
                        onClick={handleApprove}
                        disabled={loading.approve}
                        className="flex min-w-[110px] items-center justify-center rounded-lg bg-[#c51369] px-6 py-2 text-white hover:bg-[#e24692]"
                      >
                        {loading.approve ? <BtnLoader /> : "Confirm Approve"}
                      </button>

                      <button
                        onClick={() => {
                          setShowApproveFields(false);
                          setApproveErrors({
                            beneficiaryName: "",
                            upiVerified: "",
                          });
                        }}
                        className="flex min-w-[110px] items-center justify-center rounded-lg bg-gray-300 px-6 py-2 hover:bg-gray-400"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Reject Fields */}
                {showRejectFields && (
                  <div className="mt-4 space-y-3">
                    <select
                      value={reKycFor}
                      onChange={(e) => {
                        setReKycFor(e.target.value);
                        setRejectError("");
                      }}
                      className="w-2/4 rounded border px-3 py-2"
                    >
                      <option value="">Select ReKYC Reason</option>
                      <option value="PAN mismatch">PAN mismatch</option>
                      <option value="UPI invalid">UPI invalid</option>
                    </select>
                    {rejectError && (
                      <p className="text-sm text-red-500">{rejectError}</p>
                    )}

                    <div className="flex justify-center gap-3">
                      <button
                        onClick={handleReject}
                        disabled={loading.reject}
                        className="flex min-w-[110px] items-center justify-center rounded-lg bg-red-600 px-6 py-2 text-white hover:bg-red-700"
                      >
                        {loading.reject ? <BtnLoader /> : "Confirm Reject"}
                      </button>

                      <button
                        onClick={() => {
                          setShowRejectFields(false);
                          setRejectError("");
                        }}
                        className="flex min-w-[110px] items-center justify-center rounded-lg bg-gray-300 px-6 py-2 hover:bg-gray-400"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Box>
    </Modal>
  );
};

const Detail = ({ label, value }) => (
  <div className="flex justify-between border-b pb-2">
    <label className="text-gray-600">{label}</label>
    <span className="font-medium">{value || "-"}</span>
  </div>
);

export default KycPopup;
